import styled from "styled-components";
import { Utils } from "@tiffin/app-common";
import { AdvanceDurationType, convertToTime } from "@tiffin/core";
import { useAppSelector } from "../../hooks";

import { Divider, Drawer } from "antd";
import { useMediaQuery } from "react-responsive";

export const StoreDetailsDrawer = styled(BaseStoreDetailsDrawer)`

  .about-us {
    margin-top: 0px;
    color: var(--color-dark);
  }

  .section {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .subtitle {
    font-weight: 600;
    font-size: var(--font-size-medium);
    color: var(--color-dark);
  }

  .day {
    display: flex;
    justify-content: space-between;
  }

  .secondary-info {
    padding-top: 10px;
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: var(--font-size-small);
    color: var(--color-medium);
  }

  .delivery-fee-details {
    display: flex;
    flex-direction: column;
    gap: 5px;
    padding-top: 10px;

    .row {
      display: flex;
      justify-content: space-between;
      font-size: 0.875em;
    }
  }

  .delivery-area {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-top: 20px;

    .delivery-area-text {
      display: flex;
      flex-direction: column;
      gap: 5px;
      font-size: 0.875em;
      line-height: 1.6em;
    }
  }

  .prep-time {
    font-size: 0.875em;
  }
`

interface IStoreDetailsDrawer {
  className?: string,
  open: boolean,
  onClose: () => void
}

function BaseStoreDetailsDrawer({className, open, onClose}: IStoreDetailsDrawer) {

  const partner = useAppSelector((state) => state.store)
  
  const fullWidth = useMediaQuery({ query: '(max-width:500px)' })

  let maxDistanceMeters = partner.details?.deliveryConfiguration?.baseDistanceMeters ?? 0
  if(partner.details?.deliveryConfiguration?.tiers) {
    for(const tier of partner.details?.deliveryConfiguration?.tiers) {
      maxDistanceMeters += tier.distanceMeters
    }
  }

  function prepartionText() {
    if(partner.details?.preparationEnabled) {
      let text = `All orders must be placed ${partner.details?.preparationDuration} `
      if(partner.details?.preparationDurationType === AdvanceDurationType.Days) {
        if(partner.details?.preparationDuration === 1) {
          text += `day in advance`
        } else {
          text += `days in advance`
        }
      } else if(partner.details?.preparationDurationType === AdvanceDurationType.Hours) {
        if(partner.details?.preparationDurationType === 1) {
          text += 'hour in advance'
        } else {
          text += 'hours in advance'
        }
      }
      return (
        <span className="prep-time">
          {text}
        </span>
      );
    }
  }

  return (
    <>
      {
        partner.details &&
        <Drawer className={className} title={partner.details.name} placement="right" onClose={onClose} open={open} width={fullWidth ? '100%' : '500px'}>
          {
            partner.details.longDescription ?
            <>
              <h3 className="about-us">About Us</h3>
              <p>{partner.details.longDescription}</p>
            </>
            :
            <p>{partner.details.description}</p>
          }
          <div className="secondary-info">
            {partner.details.cuisines && partner.details.cuisines.map((cuisine: string, i: number) => {
              return `${i > 0 ? ' • ' : ''}${cuisine}`
            })}
          </div>
          <Divider />
          {
            partner.details.customerContactPhone &&
            <>
              <span className="subtitle">Contact Us</span>
              <p>
                If you have any questions, please call us on <a href={`tel:${partner.details.customerContactPhone}`}>{partner.details.customerContactPhone}</a>
              </p>
              <p>
              {
                  partner.details.customerContactEmail &&
                  <>
                    or drop us an email at <a href={`mailto:${partner.details.customerContactEmail}`}>{partner.details.customerContactEmail}</a>
                  </>
                }
              </p>
            </>
          }
          <Divider />
          {
            partner.details.offerPickup &&
            <div className="section">
              <span className="subtitle">Pickup Hours</span>
              {
                partner.details.pickupDays && Object.entries(partner.details.pickupDays).map(([key, value]) => {
                  const startTime = convertToTime(value.start)
                  const endTime = convertToTime(value.end)

                  return (
                    <div key={key} className="day">
                      <span>{Utils.getDayString(Number(key))}</span>
                      <span>{startTime.formatted12} - {endTime.formatted12}</span>
                    </div>
                  )
                })
              }
              <Divider />
            </div>
          }
          {
            partner.details.offerDelivery &&
            <div className="section">
              <span className="subtitle">Delivery Hours</span>
              {
                partner.details.deliveryDays && Object.entries(partner.details.deliveryDays).map(([key, value]) => {
                  const startTime = convertToTime(value.start)
                  const endTime = convertToTime(value.end)

                  return (
                    <div key={key} className="day">
                      <span>{Utils.getDayString(Number(key))}</span>
                      <span>{startTime.formatted12} - {endTime.formatted12}</span>
                    </div>
                  )
                })
              }
              {
                partner.details.deliveryDays &&
                <div className="delivery-fee-details">
                  <div className="row">
                    <div>Delivery Fee</div>
                    <div>{partner.details.deliveryFee ? `$${partner.details.deliveryFee.toFixed(2)}` : 'Free'}</div>
                  </div>
                  {
                    partner.details.minDeliveryOrder !== undefined &&
                    <div className="row">
                      {
                        partner.details.minDeliveryOrder === 0 ? <span>No minimum order</span> : (
                          <>
                            <div>Minimum Order</div>
                            <div>${partner.details.minDeliveryOrder.toFixed(2)}</div>
                          </>
                        )
                      }
                    </div>
                  }
                </div>
              }
              {
                partner.details?.offerDelivery &&
                <div className="delivery-area">
                  <div className="subtitle">Delivery Range</div>
                  <div className="delivery-area-text">
                    <span>
                      Delivery is available up to <b>{maxDistanceMeters/1000} kilometres</b> away
                    </span>
                    <span>
                      Delivery fees will be calculated at checkout
                    </span>
                  </div>
                </div>
              }
              <Divider />
            </div>
          }
          <p>{prepartionText()}</p>
        </Drawer>
      }
    </>
  )
}